import { AnimatePresence } from 'framer-motion';
import subMonths from 'date-fns/subMonths';
import React, { ChangeEventHandler, useEffect, useMemo, useState } from 'react';
import { Range } from 'react-date-range';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { RowSelectionState } from '@tanstack/react-table';
import Datepicker from '../../components/atoms/Fields/Datepicker';
import { DatepickerPosition } from '../../components/atoms/Fields/Datepicker/enums';
import DataTable from '../../components/molecules/Table/dataTable';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import { GetOrdersResponseSingleItem } from '../../setup/apiTypes/orders';
import apiRoutes from '../../setup/consts/apiRoutes';
import { ordersTableColumns } from './consts';
import {
  useExportOrders,
  useFailedOrdersMoney,
  useFailedReasonBreakdown,
  usePlaceholderProps,
  useBulkCancelOrders,
} from './hooks';
import TrendChart from './TrendChart';
import {
  categorizeReason,
  REASON_CATEGORY_DEFAULTS,
  REASON_CATEGORY_MESSAGE_IDS,
} from './reasonCategories';
import { formatIqd } from '../../utils/money';
import {
  StyledActiveReasonPill,
  StyledBreakdownBarFill,
  StyledBreakdownBarTrack,
  StyledBreakdownCount,
  StyledBreakdownHeader,
  StyledBreakdownHint,
  StyledBreakdownPct,
  StyledBreakdownRow,
  StyledBreakdownRows,
  StyledBreakdownSection,
  StyledShowLessButton,
  StyledBreakdownSkeleton,
  StyledBreakdownSkeletonRow,
  StyledBreakdownTitle,
  StyledBreakdownReasonBlock,
  StyledBreakdownReasonAr,
  StyledBreakdownReasonEn,
  StyledStatGrid,
  StyledStatLabel,
  StyledStatValue,
  StyledStatUnit,
  StyledStatSub,
  StyledCityBadgesRow,
  StyledCityBadgePill,
  StyledForm,
  StyledSegmentGroup,
  StyledSegmentButton,
  StyledBreakdownHeaderActions,
  StyledTableWrapper,
  StyledTrendBadge,
  StyledInfoTooltipWrapper,
  StyledBulkBar,
  StyledBulkBarLabel,
  StyledBulkCancelButton,
  StyledSkeletonPill,
} from './styles';
import {
  StyledToolbar,
  StyledToolbarActions,
  StyledToolbarFilters,
  StyledToolbarGhostButton,
  StyledToolbarSearch,
} from '../../components/molecules/Toolbar';
import OrderSummaryModal from '../../components/organisms/Modal/templates/OrderSummaryModal';
import useToggle from '../../hooks/useToggle';
import { DataTableAPI } from '../../components/molecules/Table/types';
import { utcToLocal } from '../../utils/date';
import ConfirmationModal from '../../components/organisms/Modal/templates/ConfirmationModal';
import Input from '../../components/atoms/Fields/Input';
import { FieldMode } from '../../components/atoms/Fields/enums';
import { IconClose, IconDownload, IconInfo, IconSearch } from '../../assets/svg/icons';
import Spinner from '../../components/atoms/Spinner';
import { SpinnerMode, SpinnerSize } from '../../components/atoms/Spinner/enums';
import { StyledClearButton } from '../../components/atoms/Fields/Autocomplete/styles';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import ScCard from '../../components/atoms/ScCard';
import Button from '../../components/atoms/Button';
import { ButtonKind } from '../../components/atoms/Button/enums';

const REASON_TIPS: Record<string, string> = {
  'لا يرد':
    "Customer isn't answering. Try WhatsApp or call at a different time (early morning or evening work best).",
  'لا يرد بعد الاتفاق':
    "Customer agreed but stopped responding. Send a WhatsApp reminder with the delivery window and driver's name.",
  'رفض من الزبون':
    'Customer refused on delivery. Usually signals a product mismatch — check your product images and descriptions match reality.',
  'الطلب غير مطابق':
    "Product didn't match the order. Audit your pack process — wrong item shipped or quantity wrong?",
};

const REASON_TIPS_EN: Record<string, string> = {
  'لا يرد': 'No answer',
  'لا يرد بعد الاتفاق': 'No answer after agreement',
  'رفض من الزبون': 'Refused by customer',
  'الطلب غير مطابق': 'Order mismatch',
  'العنوان خاطئ': 'Wrong address',
  'مؤجل': 'Postponed',
  'ملغي': 'Cancelled',
};

const GENERIC_TIP =
  'Review these orders one by one and either re-place or cancel to clear the backlog.';

const defaultRange = {
  startDate: subMonths(new Date(), 1),
  endDate: new Date(),
};

// Latin digits both locales — storefront money convention (M-20).
const compactIqd = (n: number): string => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1).replace(/\.0$/, '')}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}K`;
  return String(Math.round(n));
};

const FailedOrdersPage: React.FC = () => {
  const [range, setRange] = useState<Range>(defaultRange);
  const [detailsOrderId, setDetailsOrderId] = useState<string>();
  const [ordersTableAPI, setOrdersTableAPI] = useState<DataTableAPI>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [showAllReasons, setShowAllReasons] = useState(false);
  const [valueMode, setValueMode] = useState<'count' | 'iqd'>('iqd');

  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();
  const tableColumns = useMemo(() => ordersTableColumns(intl), [intl]);
  const { title, description, Icon } = usePlaceholderProps();

  const [debouncedQuery] = useDebounce(searchQuery, 300);
  const { handleDownloadOrdersExcelFile, isLoading: exportLoading } =
    useExportOrders();
  const { isLoading: bulkCancelLoading, bulkCancel } = useBulkCancelOrders();
  const [
    exportConfirmationModalIsOpen,
    { toggle: toggleExportConfirmationModal },
  ] = useToggle();

  const selectedOrderIds = Object.keys(rowSelection).filter(
    (k) => rowSelection[k]
  );
  const selectedCount = selectedOrderIds.length;

  const { breakdown, isLoading: breakdownLoading } = useFailedReasonBreakdown({
    from: range.startDate,
    to: range.endDate,
  });

  const { money, isLoading: moneyLoading } = useFailedOrdersMoney({
    from: range.startDate,
    to: range.endDate,
  });

  const iqdUnit = intl.formatMessage({
    id: 'failedOrders.iqdUnit',
    defaultMessage: 'IQD',
  });

  const formatDurationShort = (ms: number): string => {
    const totalHours = Math.floor(ms / 3_600_000);
    if (totalHours < 1) {
      return intl.formatMessage(
        { id: 'failedOrders.durationM', defaultMessage: '{m}m' },
        { m: Math.max(1, Math.round(ms / 60_000)) }
      );
    }
    if (totalHours < 24) {
      return intl.formatMessage(
        { id: 'failedOrders.durationH', defaultMessage: '{h}h' },
        { h: totalHours }
      );
    }
    return intl.formatMessage(
      { id: 'failedOrders.durationDh', defaultMessage: '{d}d {h}h' },
      { d: Math.floor(totalHours / 24), h: totalHours % 24 }
    );
  };

  // IQD mode needs the aggregate; fall back to counts if it failed to load.
  const effectiveMode: 'count' | 'iqd' =
    valueMode === 'iqd' && (money || moneyLoading) ? 'iqd' : 'count';

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.failedOrders',
        defaultMessage: 'Failed Orders',
      })
    );
  }, [intl, setHeaderTitle]);

  // Reset reason filter when date range changes.
  useEffect(() => {
    setSelectedReason(null);
  }, [range.startDate, range.endDate]);

  const handleSearchQueryChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearchQuery = () => setSearchQuery('');

  const onClearFiltersButtonClick = () => {
    clearSearchQuery();
    setSelectedReason(null);
    setRange(defaultRange);
  };

  const onExport = () => {
    if (exportConfirmationModalIsOpen) toggleExportConfirmationModal();
    handleDownloadOrdersExcelFile({
      statuses: ['Failed'],
      from: range.startDate && utcToLocal(range.startDate).toISOString(),
      to: range.endDate && utcToLocal(range.endDate).toISOString(),
      page: ordersTableAPI?.page || 1,
      pageSize: ordersTableAPI?.pageSize || 10,
      search: debouncedQuery,
    });
  };


  const failedCount = ordersTableAPI?.totalCount;

  const hasActiveFilters =
    range.startDate?.toDateString() !== defaultRange.startDate.toDateString() ||
    range.endDate?.toDateString() !== defaultRange.endDate.toDateString() ||
    !!debouncedQuery ||
    !!selectedReason;

  return (
    <>
      <div data-tour='failed-orders'>
      <ScPageHeader
        eyebrow={intl.formatMessage({ id: 'failedOrders.heroEyebrow', defaultMessage: 'REVENUE RECOVERY' })}
        title={
          money
            ? intl.formatMessage(
                { id: 'failedOrders.heroTitleMoney', defaultMessage: '{iqd} IQD recoverable' },
                { iqd: formatIqd(money.totalValue) }
              )
            : failedCount !== undefined
            ? intl.formatMessage({ id: 'failedOrders.heroTitle', defaultMessage: '{count} need attention' }, { count: failedCount.toLocaleString('en-US') })
            : intl.formatMessage({ id: 'mainNavigation.failedOrders', defaultMessage: 'Failed Orders' })
        }
        subtitle={intl.formatMessage({ id: 'failedOrders.heroDescription', defaultMessage: 'Each failed order is revenue waiting to be recovered. Click any reason to filter, then re-place or cancel — in seconds.' })}
        actions={
          <Button
            kind={ButtonKind.Outline}
            icon={IconDownload}
            onClick={() => {
              if (ordersTableAPI?.totalCount && ordersTableAPI.totalCount > 2000) {
                toggleExportConfirmationModal();
                return;
              }
              onExport();
            }}
            disabled={exportLoading || ordersTableAPI?.isEmpty}
          >
            <FormattedMessage id='downloadXl' defaultMessage='Export' />
          </Button>
        }
      />
      </div>

      {exportConfirmationModalIsOpen ? (
        <ConfirmationModal
          title={intl.formatMessage({ id: 'exportConfirmation' })}
          confirmButtonLabel={intl.formatMessage({ id: 'export' })}
          description={intl.formatMessage({
            id: 'ordersExportConfirmationDescription',
          })}
          cancelButtonLabel={intl.formatMessage({ id: 'cancel' })}
          withCloseButton
          onConfirmButtonClick={onExport}
          onClose={toggleExportConfirmationModal}
          loadingConfirm={exportLoading}
        />
      ) : null}

      {bulkConfirmOpen ? (
        <ConfirmationModal
          title={intl.formatMessage({ id: 'failedOrders.cancelSelectedOrders', defaultMessage: 'Cancel selected orders' })}
          description={intl.formatMessage({ id: 'failedOrders.cancelConfirmation', defaultMessage: 'Are you sure you want to cancel {count} order(s)? This action cannot be undone.' }, { count: selectedCount })}
          confirmButtonLabel={intl.formatMessage({ id: 'failedOrders.cancelOrders', defaultMessage: 'Cancel orders' })}
          cancelButtonLabel={intl.formatMessage({ id: 'failedOrders.goBack', defaultMessage: 'Go back' })}
          isWarning
          withCloseButton
          loadingConfirm={bulkCancelLoading}
          onConfirmButtonClick={() => {
            bulkCancel(selectedOrderIds, () => {
              setBulkConfirmOpen(false);
              setRowSelection({});
              ordersTableAPI?.refetch();
            });
          }}
          onClose={() => setBulkConfirmOpen(false)}
        />
      ) : null}


      {/* Hero stat trio — real IQD, real median, real cities (M-21) */}
      <StyledStatGrid>
        <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
          <StyledStatLabel>{intl.formatMessage({ id: 'failedOrders.recoverableRevenue', defaultMessage: 'Recoverable Revenue' })}</StyledStatLabel>
          <StyledStatValue $danger>
            {money ? formatIqd(money.totalValue) : moneyLoading ? '…' : '—'}
            {money && <StyledStatUnit>{iqdUnit}</StyledStatUnit>}
          </StyledStatValue>
          <StyledStatSub>
            {money
              ? intl.formatMessage(
                  { id: 'failedOrders.recoverableSub', defaultMessage: '{count} failed orders this period.' },
                  { count: money.totalCount.toLocaleString('en-US') }
                )
              : intl.formatMessage({ id: 'failedOrders.failedOrdersPendingRecovery', defaultMessage: 'Failed orders pending recovery this period.' })}
            {money?.partial &&
              ' ' +
                intl.formatMessage(
                  { id: 'failedOrders.partialNote', defaultMessage: 'Value from the {covered} most recent of {total} orders.' },
                  {
                    covered: money.coveredCount.toLocaleString('en-US'),
                    total: money.totalCount.toLocaleString('en-US'),
                  }
                )}
          </StyledStatSub>
        </ScCard>

        <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
          <StyledStatLabel>{intl.formatMessage({ id: 'failedOrders.medianTimeToFail', defaultMessage: 'Median Time to Fail' })}</StyledStatLabel>
          <StyledStatValue $danger={false}>
            {money?.medianMsToFail != null
              ? formatDurationShort(money.medianMsToFail)
              : moneyLoading
              ? '…'
              : '—'}
          </StyledStatValue>
          <StyledStatSub>{intl.formatMessage({ id: 'failedOrders.medianTimeToFailSub', defaultMessage: 'From placed to last update.' })}</StyledStatSub>
        </ScCard>

        <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
          <StyledStatLabel>{intl.formatMessage({ id: 'failedOrders.failedByCity', defaultMessage: 'Failed Orders by City · Top 4' })}</StyledStatLabel>
          <StyledCityBadgesRow>
            {money && money.cities.length > 0 ? (
              money.cities.slice(0, 4).map((c) => (
                <StyledCityBadgePill
                  key={c.city}
                  as='span'
                  $active={false}
                  style={{ cursor: 'default' }}
                >
                  <span style={{ textTransform: 'capitalize' }}>{c.city}</span>
                  <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                    {c.count.toLocaleString('en-US')}
                  </span>
                </StyledCityBadgePill>
              ))
            ) : moneyLoading ? (
              <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{intl.formatMessage({ id: 'dashboard.loading', defaultMessage: 'Loading…' })}</span>
            ) : (
              <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{intl.formatMessage({ id: 'failedOrders.noDataForPeriod', defaultMessage: 'No data for this period' })}</span>
            )}
          </StyledCityBadgesRow>
        </ScCard>
      </StyledStatGrid>

      {/* Failure reason breakdown — ranked by lost IQD value (WOW-4),
          Counts mode falls back to the analytics endpoint (all attempts). */}
      <StyledBreakdownSection>
        <StyledBreakdownHeader>
          <StyledBreakdownTitle>{intl.formatMessage({ id: 'failedOrders.whyAreOrdersFailing', defaultMessage: 'Why are orders failing?' })}</StyledBreakdownTitle>
          <StyledBreakdownHeaderActions>
            <StyledBreakdownHint>{intl.formatMessage({ id: 'failedOrders.clickReasonHint', defaultMessage: 'Click a reason to filter the table' })}</StyledBreakdownHint>
            <StyledSegmentGroup role='group' aria-label={intl.formatMessage({ id: 'failedOrders.viewModeLabel', defaultMessage: 'Breakdown mode' })}>
              <StyledSegmentButton
                type='button'
                $active={valueMode === 'iqd'}
                aria-pressed={valueMode === 'iqd'}
                onClick={() => setValueMode('iqd')}
              >
                {intl.formatMessage({ id: 'failedOrders.viewIqdValue', defaultMessage: 'IQD value' })}
              </StyledSegmentButton>
              <StyledSegmentButton
                type='button'
                $active={valueMode === 'count'}
                aria-pressed={valueMode === 'count'}
                onClick={() => setValueMode('count')}
              >
                {intl.formatMessage({ id: 'failedOrders.viewCounts', defaultMessage: 'Counts' })}
              </StyledSegmentButton>
            </StyledSegmentGroup>
          </StyledBreakdownHeaderActions>
        </StyledBreakdownHeader>

        {(() => {
          const isValueMode = effectiveMode === 'iqd';
          const rowsLoading = isValueMode ? moneyLoading && !money : breakdownLoading;
          type DisplayRow = {
            reason: string;
            amount: number;
            amountLabel: string;
            pct: number;
            trend?: 'up' | 'down' | 'stable';
          };
          const rows: DisplayRow[] = isValueMode
            ? (money?.reasons ?? []).map((r) => ({
                reason: r.reason,
                amount: r.value,
                amountLabel: formatIqd(r.value),
                pct: r.valuePct,
              }))
            : breakdown.map((b) => ({
                reason: b.reason,
                amount: b.count,
                amountLabel: b.count.toLocaleString('en-US'),
                pct: b.pct,
                trend: b.trend,
              }));

          if (rowsLoading) {
            return (
              <StyledBreakdownSkeleton>
                {[100, 65, 40, 25].map((w) => (
                  <StyledBreakdownSkeletonRow key={w}>
                    <StyledSkeletonPill width={`${w}%`} />
                    <StyledSkeletonPill />
                    <StyledSkeletonPill width='2rem' />
                    <StyledSkeletonPill width='2.5rem' />
                  </StyledBreakdownSkeletonRow>
                ))}
              </StyledBreakdownSkeleton>
            );
          }

          if (rows.length === 0) {
            return (
              <StyledBreakdownHint>{intl.formatMessage({ id: 'failedOrders.noBreakdownData', defaultMessage: 'No failure reason data for this period.' })}</StyledBreakdownHint>
            );
          }

          const TOP = 5;
          const visible = showAllReasons ? rows : rows.slice(0, TOP);
          const rest = rows.slice(TOP);
          const otherAmount = rest.reduce((s, r) => s + r.amount, 0);
          const otherPct = rest.reduce((s, r) => s + r.pct, 0);
          const otherAmountLabel = isValueMode
            ? formatIqd(otherAmount)
            : otherAmount.toLocaleString('en-US');

          return (
            <StyledBreakdownRows $scrollable={showAllReasons}>
              {visible.map(({ reason, amountLabel, pct, trend }) => {
                const category = categorizeReason(reason);
                const englishLabel =
                  REASON_TIPS_EN[reason] ??
                  intl.formatMessage({
                    id: REASON_CATEGORY_MESSAGE_IDS[category],
                    defaultMessage: REASON_CATEGORY_DEFAULTS[category],
                  });
                return (
                  <StyledBreakdownRow
                    key={reason}
                    active={selectedReason === reason}
                    onClick={() =>
                      setSelectedReason((prev) => (prev === reason ? null : reason))
                    }
                  >
                    <StyledBreakdownReasonBlock>
                      <StyledBreakdownReasonAr className='ar' title={reason} $active={selectedReason === reason}>{reason}</StyledBreakdownReasonAr>
                      {intl.locale !== 'ar' && (
                        <StyledBreakdownReasonEn title={englishLabel}>{englishLabel}</StyledBreakdownReasonEn>
                      )}
                    </StyledBreakdownReasonBlock>
                    <StyledBreakdownBarTrack>
                      <StyledBreakdownBarFill pct={pct} />
                    </StyledBreakdownBarTrack>
                    <StyledBreakdownCount>
                      {amountLabel}
                      {trend && trend !== 'stable' && (
                        <StyledTrendBadge trend={trend}>
                          {trend === 'up' ? '↑' : '↓'}
                        </StyledTrendBadge>
                      )}
                    </StyledBreakdownCount>
                    <StyledBreakdownPct>{pct}%</StyledBreakdownPct>
                    <StyledInfoTooltipWrapper
                      data-tip={REASON_TIPS[reason] ?? GENERIC_TIP}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconInfo />
                    </StyledInfoTooltipWrapper>
                  </StyledBreakdownRow>
                );
              })}

              {!showAllReasons && rest.length > 0 && (
                <StyledBreakdownRow
                  type='button'
                  active={false}
                  onClick={() => setShowAllReasons(true)}
                  style={{ opacity: 0.7 }}
                >
                  <StyledBreakdownReasonBlock>
                    <StyledBreakdownReasonEn>{intl.formatMessage({ id: 'failedOrders.showAllReasons', defaultMessage: 'Show all {count} reasons' }, { count: rows.length })}</StyledBreakdownReasonEn>
                  </StyledBreakdownReasonBlock>
                  <StyledBreakdownBarTrack>
                    <StyledBreakdownBarFill pct={otherPct} />
                  </StyledBreakdownBarTrack>
                  <StyledBreakdownCount>{otherAmountLabel}</StyledBreakdownCount>
                  <StyledBreakdownPct>{otherPct}%</StyledBreakdownPct>
                </StyledBreakdownRow>
              )}

              {showAllReasons && rest.length > 0 && (
                <StyledShowLessButton
                  type='button'
                  onClick={() => setShowAllReasons(false)}
                >
                  {intl.formatMessage({ id: 'failedOrders.showLess', defaultMessage: 'Show less' })}
                </StyledShowLessButton>
              )}
            </StyledBreakdownRows>
          );
        })()}
      </StyledBreakdownSection>

      {/* Daily failed-value trend — honest numbers, real dates (WOW-4) */}
      <StyledBreakdownSection>
        <StyledBreakdownHeader>
          <StyledBreakdownTitle>
            {effectiveMode === 'iqd'
              ? intl.formatMessage({ id: 'failedOrders.trendTitleValue', defaultMessage: 'Failed value by day' })
              : intl.formatMessage({ id: 'failedOrders.trendTitleCount', defaultMessage: 'Failed orders by day' })}
          </StyledBreakdownTitle>
          <StyledBreakdownHint>{intl.formatMessage({ id: 'failedOrders.trendSub', defaultMessage: 'Grouped by the day the order was last updated.' })}</StyledBreakdownHint>
        </StyledBreakdownHeader>
        {money && money.daily.length > 1 ? (
          <TrendChart
            points={money.daily.map((d) => ({
              date: new Date(`${d.date}T00:00:00Z`),
              value: effectiveMode === 'iqd' ? d.value : d.count,
            }))}
            formatAxis={(n) =>
              effectiveMode === 'iqd' ? compactIqd(n) : Math.round(n).toLocaleString('en-US')
            }
            formatValue={(n) =>
              effectiveMode === 'iqd'
                ? `${formatIqd(n)} ${iqdUnit}`
                : intl.formatMessage(
                    { id: 'failedOrders.trendOrdersValue', defaultMessage: '{count} orders' },
                    { count: n.toLocaleString('en-US') }
                  )
            }
            locale={intl.locale}
            ariaLabel={intl.formatMessage({ id: 'failedOrders.trendTitleValue', defaultMessage: 'Failed value by day' })}
          />
        ) : moneyLoading ? (
          <StyledBreakdownSkeleton>
            {[100, 100, 100].map((w, i) => (
              <StyledBreakdownSkeletonRow key={i}>
                <StyledSkeletonPill width={`${w}%`} />
              </StyledBreakdownSkeletonRow>
            ))}
          </StyledBreakdownSkeleton>
        ) : (
          <StyledBreakdownHint>{intl.formatMessage({ id: 'failedOrders.noDataForPeriod', defaultMessage: 'No data for this period' })}</StyledBreakdownHint>
        )}
      </StyledBreakdownSection>

      <ScCard pad={false} style={{ padding: 'var(--s-4)', marginBottom: 'var(--s-4)' }}>
      <StyledToolbar style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
        <StyledToolbarSearch>
          <StyledForm onSubmit={(e) => e.preventDefault()}>
            <Input
              mode={FieldMode.Tertiary}
              placeholder={intl.formatMessage({
                id: 'search.searchOrders',
                defaultMessage: 'Search orders…',
              })}
              isRounded
              prefixComponent={<IconSearch />}
              suffixComponent={
                (ordersTableAPI?.loading && searchQuery) ||
                (debouncedQuery && !searchQuery) ? (
                  <Spinner
                    mode={SpinnerMode.Dark}
                    size={SpinnerSize.ExtraSmall}
                  />
                ) : debouncedQuery ? (
                  <StyledClearButton type='button' aria-label='Clear search' onClick={clearSearchQuery}>
                    <IconClose />
                  </StyledClearButton>
                ) : null
              }
              value={searchQuery}
              onChange={handleSearchQueryChange}
              type='input'
              disabled={ordersTableAPI?.loading}
            />
          </StyledForm>
        </StyledToolbarSearch>

        <StyledToolbarFilters>
          {/* Active reason pill — shows which reason is filtering the table */}
          {selectedReason && (
            <StyledActiveReasonPill
              type='button'
              onClick={() => setSelectedReason(null)}
              title='Clear reason filter'
            >
              {selectedReason}
              <IconClose />
            </StyledActiveReasonPill>
          )}

          <Datepicker
            value={range}
            onChange={(item) => {
              setRange(item);
            }}
            position={DatepickerPosition.Right}
          />

          {hasActiveFilters && (
            <StyledToolbarGhostButton
              type='button'
              onClick={onClearFiltersButtonClick}
            >
              <IconClose />
              <FormattedMessage
                id='search.clearFilters'
                defaultMessage='Clear'
              />
            </StyledToolbarGhostButton>
          )}
        </StyledToolbarFilters>

        <StyledToolbarActions>
          {exportLoading && (
            <Spinner mode={SpinnerMode.Dark} size={SpinnerSize.ExtraSmall} />
          )}
        </StyledToolbarActions>
      </StyledToolbar>
      </ScCard>

      {selectedCount > 0 && (
        <StyledBulkBar>
          <StyledBulkBarLabel>
            {intl.formatMessage({ id: 'failedOrders.ordersSelected', defaultMessage: '{count} orders selected' }, { count: selectedCount })}
          </StyledBulkBarLabel>
          <StyledBulkCancelButton
            type='button'
            disabled={bulkCancelLoading}
            onClick={() => setBulkConfirmOpen(true)}
          >
            {intl.formatMessage({ id: 'failedOrders.cancelSelected', defaultMessage: 'Cancel selected' })}
          </StyledBulkCancelButton>
        </StyledBulkBar>
      )}

      <ScCard pad={false}>
      <StyledTableWrapper>
        <DataTable<GetOrdersResponseSingleItem>
          endpoint={apiRoutes.ORDERS}
          tableAPIRef={setOrdersTableAPI}
          placeholderProps={{ title, description, icon: Icon }}
          columns={tableColumns}
          rowSelection={rowSelection}
          setSelectedRows={setRowSelection}
          getRowId={(row) => row.id}
          query={{
            ...(range.startDate
              ? { from: utcToLocal(range.startDate).toISOString() }
              : {}),
            ...(range.endDate
              ? { to: utcToLocal(range.endDate).toISOString() }
              : {}),
            ...(debouncedQuery ? { search: debouncedQuery } : {}),
            ...(selectedReason ? { failureReason: selectedReason } : {}),
            statuses: ['Failed'],
          }}
          onRowClick={({ original }) => setDetailsOrderId(original.id)}
          queryOptions={{ cacheTime: 0 }}
        />
        <AnimatePresence>
          {detailsOrderId ? (
            <OrderSummaryModal
              orderId={detailsOrderId}
              refetchOrders={ordersTableAPI?.refetch}
              onClose={() => setDetailsOrderId(undefined)}
            />
          ) : null}
        </AnimatePresence>
      </StyledTableWrapper>
      </ScCard>
    </>
  );
};

export default FailedOrdersPage;
